import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "@/frontend/lib/utils";
import { Button } from "@/frontend/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/frontend/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/frontend/components/ui/popover";

interface TypologyComboboxProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  /** Existing typology keys for the current residence. */
  options: string[];
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Creatable combobox for a room's optional typology key. The admin can pick an
 * existing typology of the same residence, type a brand-new one, or clear it.
 */
export function TypologyCombobox({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "No typology",
}: TypologyComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const trimmedSearch = search.trim();

  const filtered = React.useMemo(() => {
    const lower = trimmedSearch.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(lower));
  }, [options, trimmedSearch]);

  const exactMatch = options.some(
    (o) => o.toLowerCase() === trimmedSearch.toLowerCase()
  );
  const showCreate = trimmedSearch.length > 0 && !exactMatch;

  const select = (val: string | undefined) => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal hover:bg-transparent hover:text-foreground"
          disabled={disabled}
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type a new typology..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No typologies found.</CommandEmpty>
            {(value || filtered.length > 0) && (
              <CommandGroup>
                {value && (
                  <CommandItem
                    value="__clear__"
                    onSelect={() => select(undefined)}
                    className="cursor-pointer text-muted-foreground"
                  >
                    <X className="mr-2 h-4 w-4" />
                    No typology
                  </CommandItem>
                )}
                {filtered.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => select(option)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showCreate && (
              <CommandGroup>
                <CommandItem
                  value={`__create__${trimmedSearch}`}
                  onSelect={() => select(trimmedSearch)}
                  className="cursor-pointer"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create "{trimmedSearch}"
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
